import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Paper, Switch, Typography } from "@mui/material";
import { IPlan } from "../../types/Plan";
import JornadaService from "../../services/JornadaService";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import ConfigUtils from "../../utils/Config";
import * as constantes from "../../common/Constantes";

// Bloque «Registro de jornada» de Configuración › Usuarios: activar o desactivar el módulo. Con Starter no se puede.
interface RegistroJornadaSwitchProps {
    plan?: IPlan;
    onError: (mensaje: string) => void;
}

// Planes con registro de jornada. De momento, solo los testers; para abrirlo al resto, añadir 'Team' y 'Master' (y lo mismo
// en PLANES_JORNADA de la API, utils/plan.ts)
export const PLANES_JORNADA = ['Tester'];
export const planPermiteJornada = (plan?: IPlan) => !!plan && PLANES_JORNADA.some(nombre => plan.name.includes(nombre));

const RegistroJornadaSwitch = ({ plan, onError }: RegistroJornadaSwitchProps) => {
    const history = useNavigate();
    const [activo, setActivo] = useState<boolean>(ConfigUtils.getRegistroJornada()?.activo ?? false);
    const [loading, setLoading] = useState(false);
    const [modalActivado, setModalActivado] = useState(false);
    const [modalDesactivar, setModalDesactivar] = useState(false);
    const planValido = planPermiteJornada(plan);

    const cambiar = (nuevo: boolean) => {
        setLoading(true);
        JornadaService.updateModulo(nuevo)
            .then(() => {
                setActivo(nuevo);
                ConfigUtils.setRegistroJornadaActivo(nuevo);
                // La barra vuelve a leer la configuración y muestra u oculta el icono (como en Servicios o WhatsApp)
                window.dispatchEvent(new Event('popstate'));
                if (nuevo) setModalActivado(true);
            })
            .catch((e: unknown) => {
                const fallo = e as { message?: string; response?: { status?: number; data?: { message?: string } } };
                if (fallo.response?.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else if (fallo.response?.status === 403 || fallo.response?.status === 400) {
                    onError(fallo.response.data?.message || 'No se ha podido cambiar el registro de jornada');
                } else {
                    const error: ILog = { method: 'RegistroJornadaSwitch.updateModulo', level: 'error', message: fallo.message ?? '', meta: fallo.response };
                    LogService.logError(error);
                    onError(fallo.response?.data?.message || 'No se ha podido cambiar el registro de jornada');
                }
            })
            .finally(() => {
                setLoading(false);
                setModalDesactivar(false);
            });
    };

    // Con un plan sin el módulo no se muestra el bloque (salvo que ya estuviera activo, para poder desactivarlo)
    if (!planValido && !activo) return null;

    return (
        <Paper elevation={2} sx={{ mt: 2, p: 3 }}>
            <Typography variant="h6">Registro de jornada</Typography>
            <Typography variant="body2" color="text.secondary">Tu equipo ficha desde Zyra y tú tienes su registro horario listo para consultar y exportar.</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControlLabel
                    label={activo ? 'Activado' : 'Desactivado'}
                    control={<Switch checked={activo} disabled={loading || (!planValido && !activo)} onChange={(event) => event.target.checked ? cambiar(true) : setModalDesactivar(true)} />}
                />
                {loading && <CircularProgress size={20} />}
            </Box>

            {activo && (
                <Button variant="text" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => history(constantes.R_JORNADA)}>Ir a Registro de jornada</Button>
            )}

            {/* AL ACTIVAR */}
            <Dialog open={modalActivado} maxWidth="sm" onClose={() => setModalActivado(false)}>
                <DialogTitle>Registro de jornada activado</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>Tu equipo ya puede fichar su entrada, sus pausas y su salida desde Zyra.</DialogContentText>
                    <DialogContentText>Te recomendamos configurar ahora el horario de cada persona. Con el horario, Zyra puede:</DialogContentText>
                    <Box component="ul" sx={{ mt: 1, mb: 2, color: 'text.secondary', typography: 'body1' }}>
                        <li>recordar a cada uno que fiche al empezar y al terminar su jornada;</li>
                        <li>avisarte si alguien no ha fichado;</li>
                        <li>comparar las horas previstas con las fichadas.</li>
                    </Box>
                    <DialogContentText>Puedes hacerlo más tarde desde Jornada › Configuración.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setModalActivado(false); history(constantes.R_JORNADA); }} color="primary">CONFIGURAR HORARIOS</Button>
                    <Button onClick={() => setModalActivado(false)} color="primary" autoFocus>MÁS TARDE</Button>
                </DialogActions>
            </Dialog>

            {/* AL DESACTIVAR */}
            <Dialog open={modalDesactivar} maxWidth="sm" onClose={() => setModalDesactivar(false)}>
                <DialogTitle>¿Desactivar el registro de jornada?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Tu equipo dejará de fichar y de recibir recordatorios. Los registros guardados se conservan: podrás consultarlos y exportarlos cuando quieras, y volver a activarlo sin perder nada.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => cambiar(false)} color="primary" disabled={loading}>DESACTIVAR</Button>
                    <Button onClick={() => setModalDesactivar(false)} color="primary" autoFocus>CANCELAR</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default RegistroJornadaSwitch;
